// @file Helper for grouping posts
import { POST_COLOR_STRINGS } from '@@/bits/post_color'
import { WallWishGroupByTypes } from '@@/enums'
import type { Post, PostColorId } from '@@/types'

type GroupByNone = Lowercase<WallWishGroupByTypes.None>
interface Group {
  id: string | number
  [k: string]: any
}

const BLANK_GROUP: Group = { id: WallWishGroupByTypes.None }

const ORDERED_COLOR_GROUPS = POST_COLOR_STRINGS.map((c) => {
  return {
    id: c,
  }
})

// Helper function to filter posts by color
const filterPostsByColor = (posts: Post[], colorValue: PostColorId): Post[] => {
  return posts.filter((post) => (post.color == null ? colorValue === 'default' : colorValue === post.color))
}

// Helper function to filter posts by section
const filterPostsBySection = (posts: Post[], sectionId: number): Post[] => {
  return posts.filter((post) => post.wall_section_id === sectionId)
}

// Helper function to filter posts by generic property
const filterPostsByProperty = (posts: Post[], propertyKey: keyof Post, value: string | number): Post[] => {
  return posts.filter((post) => String(post[propertyKey]) === String(value))
}

const getPostsByGroupByProperty = <T extends Group['id']>(
  posts: Post[],
  sortedGroupByValues: T[],
  groupBy: WallWishGroupByTypes,
): { [K in T | GroupByNone]?: Post[] } => {
  if (groupBy === WallWishGroupByTypes.None) {
    return {
      none: posts,
    }
  }

  return sortedGroupByValues.reduce<{ [K in T | GroupByNone]?: Post[] }>((obj, groupByValue) => {
    let filteredPosts: Post[]

    switch (groupBy) {
      case WallWishGroupByTypes.Color:
        filteredPosts = filterPostsByColor(posts, groupByValue as PostColorId)
        break
      case WallWishGroupByTypes.Section:
        filteredPosts = filterPostsBySection(posts, groupByValue as number)
        break
      default:
        filteredPosts = filterPostsByProperty(posts, groupBy as keyof Post, groupByValue)
        break
    }

    return {
      ...obj,
      [groupByValue]: filteredPosts,
    }
  }, {})
}

export { BLANK_GROUP, getPostsByGroupByProperty, ORDERED_COLOR_GROUPS }
export type { Group }
